import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { handleError, ErrorTypes } from "../utils/errorHandler";
import { generateContextualWhatsAppLink, WHATSAPP_CONTEXT, trackWhatsAppClick } from "../utils/whatsapp";
import useAuth from "./useAuth";
import { saveAbandonedCart } from "../services/abandonedCartApi";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [cartItems, setCartItems] = useState(() => {
        const storedCart = localStorage.getItem("cartItems");
        return storedCart ? JSON.parse(storedCart) : [];
    });

    // ✅ Referência para controlar o timer de carrinho vazio
    const emptyCartTimerRef = useRef(null);
    const emptyCartNotificationShownRef = useRef(false);
    const lastCartEmptyTimeRef = useRef(null);
    
    // ✅ Referência para controlar follow-up após adicionar ao carrinho
    const followUpTimerRef = useRef(null);
    const lastAddedProductRef = useRef(null);
    const abandonTimerRef = useRef(null);

    // Atualizar o localStorage sempre que o carrinho mudar
    useEffect(() => {
        localStorage.setItem("cartItems", JSON.stringify(cartItems));
    }, [cartItems]);

    useEffect(() => {
        if (abandonTimerRef.current) {
            clearTimeout(abandonTimerRef.current);
            abandonTimerRef.current = null;
        }
        const optedIn = localStorage.getItem("emperc_cart_recovery_opt_in") === "1";
        if (!optedIn || cartItems.length === 0) return undefined;
        const email = user?.email;
        if (!email) return undefined;

        abandonTimerRef.current = setTimeout(() => {
            const totalPrice = cartItems.reduce(
                (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
                0
            );
            saveAbandonedCart(
                {
                    email,
                    items: cartItems.map((item) => ({
                        product: item.id || item._id || item.productId,
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                        image: item.image,
                    })),
                    totalPrice,
                    recoveryOptIn: true,
                },
                token
            ).catch(() => {});
        }, 60000);

        return () => {
            if (abandonTimerRef.current) {
                clearTimeout(abandonTimerRef.current);
            }
        };
    }, [cartItems, user?.email, token]);

    // ✅ Monitorar carrinho vazio e mostrar mensagem após tempo determinado
    useEffect(() => {
        // Limpar timer anterior se existir
        if (emptyCartTimerRef.current) {
            clearTimeout(emptyCartTimerRef.current);
            emptyCartTimerRef.current = null;
        }

        if (cartItems.length === 0) {
            // Se o carrinho está vazio, verificar se já foi notificado
            const now = Date.now();
            
            // Se não há timestamp anterior, registrar agora
            if (!lastCartEmptyTimeRef.current) {
                lastCartEmptyTimeRef.current = now;
            }

            // Se já foi notificado, não notificar novamente nesta sessão
            if (emptyCartNotificationShownRef.current) {
                return;
            }

            // Tempo em milissegundos (30 minutos = 30 * 60 * 1000)
            const EMPTY_CART_NOTIFICATION_DELAY = 30 * 60 * 1000; // 30 minutos
            
            const timeSinceEmpty = now - lastCartEmptyTimeRef.current;

            // Se já passou o tempo, mostrar notificação imediatamente
            if (timeSinceEmpty >= EMPTY_CART_NOTIFICATION_DELAY) {
                showEmptyCartNotification();
            } else {
                // Agendar notificação para quando completar o tempo
                const remainingTime = EMPTY_CART_NOTIFICATION_DELAY - timeSinceEmpty;
                emptyCartTimerRef.current = setTimeout(() => {
                    showEmptyCartNotification();
                }, remainingTime);
            }
        } else {
            // Carrinho não está mais vazio, resetar
            lastCartEmptyTimeRef.current = null;
            emptyCartNotificationShownRef.current = false;
        }

        // Cleanup ao desmontar
        return () => {
            if (emptyCartTimerRef.current) {
                clearTimeout(emptyCartTimerRef.current);
            }
        };
    }, [cartItems]);

    // ✅ Função para mostrar notificação de follow-up após adicionar ao carrinho
    const showFollowUpNotification = (product) => {
        const isPT = navigator.language.startsWith("pt");
        
        try {
            const { FOLLOW_UP_MESSAGES, isProductOnSale } = require('../config/whatsappMessages');
            
            // Tentar obter nome do usuário do localStorage ou contexto
            let nomeCliente = "visitante";
            try {
                const userData = localStorage.getItem('user');
                if (userData) {
                    const parsed = JSON.parse(userData);
                    nomeCliente = parsed.name || parsed.email || "visitante";
                }
            } catch (e) {
                // Usar padrão
            }
            
            const productOnSale = isProductOnSale(product);
            
            // Escolher mensagem apropriada
            let followUpMessage;
            if (productOnSale) {
                followUpMessage = isPT 
                    ? FOLLOW_UP_MESSAGES.PROMO_IN_CART.PT(nomeCliente)
                    : FOLLOW_UP_MESSAGES.PROMO_IN_CART.EN(nomeCliente);
            } else {
                followUpMessage = isPT 
                    ? FOLLOW_UP_MESSAGES.ADDED_TO_CART.PT(nomeCliente, product.name)
                    : FOLLOW_UP_MESSAGES.ADDED_TO_CART.EN(nomeCliente, product.name);
            }
            
            const whatsappLink = generateContextualWhatsAppLink({
                context: WHATSAPP_CONTEXT.PRODUCT,
                messageOptions: {
                    product,
                    nomeCliente,
                    isPT,
                    contextoAdicional: followUpMessage,
                }
            });
            
            toast.info(
                <div>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                        {isPT ? '💬 Dúvidas sobre a peça?' : '💬 Questions about this part?'}
                    </p>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                        {isPT 
                            ? `Fale conosco no WhatsApp sobre ${product.name}.`
                            : `Chat with us on WhatsApp about ${product.name}.`}
                    </p>
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                            trackWhatsAppClick('follow_up_cart_clicked', {
                                productId: product.id,
                                productName: product.name,
                                isOnSale: productOnSale
                            });
                        }}
                        style={{
                            display: 'inline-block',
                            padding: '8px 16px',
                            backgroundColor: '#25D366',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }}
                    >
                        {isPT ? '💬 Falar no WhatsApp' : '💬 Chat on WhatsApp'}
                    </a>
                </div>,
                {
                    position: "bottom-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                }
            );
        } catch (e) {
            // Fallback silencioso se houver erro
            console.warn('Erro ao mostrar follow-up:', e);
        }
    };

    // ✅ Função para mostrar notificação de carrinho vazio
    const showEmptyCartNotification = () => {
        if (emptyCartNotificationShownRef.current) return;
        
        emptyCartNotificationShownRef.current = true;
        const isPT = navigator.language.startsWith("pt");
        
        const whatsappLink = generateContextualWhatsAppLink({
            context: WHATSAPP_CONTEXT.CATALOG,
            messageOptions: {
                nomeCliente: "visitante",
                isPT,
                contextoAdicional: isPT 
                    ? "Estou navegando no catálogo e gostaria de ajuda para encontrar produtos ideais para mim. 😊"
                    : "I'm browsing the catalog and would like help finding the perfect products for me. 😊"
            }
        });

        toast.info(
            <div>
                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                    {isPT ? '💡 Precisa de ajuda?' : '💡 Need help?'}
                </p>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                    {isPT 
                        ? 'Nossa equipe está pronta para te ajudar a encontrar os produtos ideais!'
                        : 'Our team is ready to help you find the perfect products!'}
                </p>
                <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                        trackWhatsAppClick('empty_cart_notification_clicked', {
                            timeSinceEmpty: Date.now() - (lastCartEmptyTimeRef.current || Date.now()),
                            page: window.location.pathname
                        });
                    }}
                    style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: '#25D366',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    {isPT ? '💬 Falar no WhatsApp' : '💬 Chat on WhatsApp'}
                </a>
            </div>,
            {
                position: "bottom-right",
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            }
        );
    };

    const checkProductStock = async (productId, quantity) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/products/check-stock/${productId}`,
                { params: { quantity } }
            );
            return response.data; // Retorna { available: true/false, stock: number, price: number }
        } catch (error) {
            throw new Error("Erro ao verificar estoque.");
        }
    };

    const addItemToCart = async (product) => {
        try {
            // Verifica o estoque antes de adicionar ao carrinho
            const stockData = await checkProductStock(product.id, product.quantity);
            
            // Verifica se o produto está sem estoque
            if (!stockData.available || stockData.stock === 0) {
                handleError(
                    { message: 'Produto sem estoque', type: ErrorTypes.STOCK },
                    { 
                        context: 'CartContext.addItemToCart',
                        customMessage: `${product.name} está sem estoque no momento. Por favor, escolha outro produto ou entre em contato conosco.`
                    }
                );
                return;
            }

            let itemAdded = false;
            setCartItems((prevItems) => {
                const existingItem = prevItems.find((item) => item.id === product.id);
                if (existingItem) {
                    const newQuantity = existingItem.quantity + product.quantity;
                    if (newQuantity > stockData.stock) {
                        handleError(
                            { message: 'Estoque insuficiente', type: ErrorTypes.STOCK },
                            { 
                                context: 'CartContext.addItemToCart',
                                customMessage: `Estoque insuficiente para ${product.name}. Apenas ${stockData.stock} unidade(s) disponível(eis).`
                            }
                        );
                        return prevItems;
                    }
                    itemAdded = true;
                    return prevItems.map((item) =>
                        item.id === product.id ? { ...item, quantity: newQuantity } : item
                    );
                } else {
                    itemAdded = true;
                    return [...prevItems, { ...product, price: stockData.price }];
                }
            });

            if (itemAdded) {
                toast.success(`${product.name} adicionado ao carrinho!`);
                
                // ✅ Mostrar follow-up após 5 segundos
                lastAddedProductRef.current = product;
                
                // Limpar timer anterior se existir
                if (followUpTimerRef.current) {
                    clearTimeout(followUpTimerRef.current);
                }
                
                // Agendar follow-up
                followUpTimerRef.current = setTimeout(() => {
                    showFollowUpNotification(product);
                }, 5000); // 5 segundos após adicionar
            }
        } catch (error) {
            // Verifica se o erro é relacionado a estoque
            if (error.message && error.message.includes("estoque")) {
                handleError(
                    { message: error.message, type: ErrorTypes.STOCK },
                    { 
                        context: 'CartContext.addItemToCart',
                        customMessage: `${product.name} está sem estoque no momento. Por favor, escolha outro produto ou entre em contato conosco.`
                    }
                );
            } else {
                handleError(error, {
                    context: 'CartContext.addItemToCart',
                    customMessage: `Erro ao adicionar ${product.name} ao carrinho. Tente novamente mais tarde.`,
                });
            }
        }
    };

    const updateQuantity = async (productId, newQuantity) => {
        try {
            const stockData = await checkProductStock(productId, newQuantity);
            if (!stockData.available) {
                handleError(
                    { message: 'Estoque insuficiente', type: ErrorTypes.STOCK },
                    { 
                        context: 'CartContext.updateQuantity',
                        customMessage: `Estoque insuficiente. Apenas ${stockData.stock} unidade(s) disponível(eis).`
                    }
                );
                return;
            }

            setCartItems((prevItems) =>
                prevItems.map((item) =>
                    item.id === productId ? { ...item, quantity: newQuantity } : item
                )
            );
        } catch (error) {
            handleError(error, {
                context: 'CartContext.updateQuantity',
                customMessage: 'Erro ao atualizar a quantidade do produto. Tente novamente mais tarde.',
            });
        }
    };

    const removeItemFromCart = (productId) => {
        setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
    };

    const clearCart = () => {
        setCartItems([]);
    };

    return (
        <CartContext.Provider
            value={{ cartItems, addItemToCart, updateQuantity, removeItemFromCart, clearCart }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
